import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Divider,
  Button,
  Grid,
  IconButton,
  Chip,
  Box,
  Tooltip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import { formatDisplayDate, formatMeasurementDateTime } from "../pages/PhysicalMeasurements/PhysicalMeasurementsList";

const value = (v: any, suffix = "") => {
  if (v === null || v === undefined || v === "" || v === "-" || v === "N/A") return "-";
  return `${v}${suffix}`;
};

const formatDate = (date?: any) => formatDisplayDate(date);
const formatDateTime = (date?: any) => formatMeasurementDateTime(date);

const MeasurementDetailsDialog = ({ open, onClose, row }: any) => {
  if (!row) return null;

  const handleDownload = () => {
    window.print();
  };

  const name = row.name || row.fullName || row.full_name || "-";
  const gender = row.gender || "-";
  const dob = row.dob || row.dateOfBirth || row.date_of_birth || "-";
  const exactAge = row.exactAgeYears > 0 ? `${row.exactAgeYears} yrs` : (row.age && row.age !== "-" ? `${row.age} yrs` : "-");
  const email = row.email || "-";
  const mobile = row.mobile || row.phone || row.mobileNumber || row.mobile_number || "-";
  const lastMeasurement = row.lastMeasurement || row.measurementDate || row.measurement_date || row.createdAt || row.created_at || "-";

  const height = row.height ?? "-";
  const weight = row.weight ?? "-";
  const bmi = row.bmi ?? "-";
  const heartRate = row.heartRate ?? row.heart_rate ?? "-";

  const isEstimated = row.isBodyFatEstimated !== false; // true if estimated from Deurenberg formula
  const bodyFat = row.bodyFat ?? row.body_fat ?? "-";
  const muscleMass = row.muscleMass ?? row.muscle_mass ?? "-";
  const boneMass = row.boneMass ?? row.bone_mass ?? "-";
  const leanBodyMass = row.leanBodyMass ?? row.lean_body_mass ?? "-";
  const bodyMoisture = row.bodyMoisture ?? row.body_moisture ?? "-";
  const protein = row.protein ?? "-";

  const visceralFat = row.visceralFat ?? row.visceral_fat ?? "-";
  const subcutaneousFat = row.subcutaneousFat ?? row.subcutaneous_fat ?? "-";
  const obesityLevel = row.obesityLevel ?? row.obesity_level ?? "-";

  const bmr = row.bmr ?? "-";
  const bmrMethod = row.bmrMethod || "Schofield / FAO-WHO-UNU";
  const standardWeight = row.standardWeight ?? row.standard_weight ?? "-";
  const standardWeightMethod = row.standardWeightMethod || "WHO P50 Reference";
  const bodyType = row.bodyType ?? row.body_type ?? "-";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle component="div" sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: "#1F497D", fontWeight: 600 }}>
        <span>Student Pediatric Measurement Details — {name !== "-" ? name : "Student"}</span>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {/* ================= BASIC INFO ================= */}
        <Typography variant="subtitle1" fontWeight={600} color="#1F497D" gutterBottom>
          Basic Information
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary" display="block">Full Name</Typography>
            <Typography variant="body1" fontWeight={500}>{value(name)}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary" display="block">Gender</Typography>
            <Typography variant="body1" fontWeight={500}>{value(gender)}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary" display="block">Date of Birth</Typography>
            <Typography variant="body1" fontWeight={500}>{formatDate(dob)}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary" display="block">Age at Measurement</Typography>
            <Typography variant="body1" fontWeight={500}>{exactAge}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary" display="block">Email Address</Typography>
            <Typography variant="body1" fontWeight={500}>{value(email)}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary" display="block">Mobile Number</Typography>
            <Typography variant="body1" fontWeight={500}>{value(mobile)}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary" display="block">Last Measurement Date & Time</Typography>
            <Typography variant="body1" fontWeight={500}>
              {formatDateTime(lastMeasurement)}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2.5 }} />

        {/* ================= ANTHROPOMETRY / PHYSICAL ================= */}
        <Typography variant="subtitle1" fontWeight={600} color="#1F497D" gutterBottom>
          Physical Measurements & WHO Growth Status
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary" display="block">Height</Typography>
            <Typography variant="body1" fontWeight={500}>{value(height, " cm")}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary" display="block">Weight</Typography>
            <Typography variant="body1" fontWeight={500}>{value(weight, " kg")}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary" display="block">Body Mass Index (BMI)</Typography>
            <Typography variant="body1" fontWeight={500}>{value(bmi, " kg/m²")}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary" display="block">Heart Rate</Typography>
            <Typography variant="body1" fontWeight={500}>{value(heartRate, " bpm")}</Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Tooltip title="WHO pediatric BMI-for-age Z-score classification (Ages 5–19)">
              <Typography variant="caption" color="text.secondary" display="block">WHO BMI-for-age Classification</Typography>
            </Tooltip>
            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
              <Chip
                label={value(obesityLevel)}
                size="small"
                color={
                  obesityLevel === "Normal" || obesityLevel === "Normal Weight"
                    ? "success"
                    : obesityLevel === "Overweight"
                    ? "warning"
                    : obesityLevel === "Obese"
                    ? "error"
                    : obesityLevel === "Severe Thinness" || obesityLevel === "Thinness"
                    ? "info"
                    : "default"
                }
                sx={{ fontWeight: 600 }}
              />
              <Typography variant="caption" color="text.secondary">
                (WHO 2007 5–19y Standard)
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Tooltip title="Reference median weight based on pediatric WHO reference methodology.">
              <Typography variant="caption" color="text.secondary" display="block">Reference Median Weight (P50)</Typography>
            </Tooltip>
            <Typography variant="body1" fontWeight={600} color="#1F497D">
              {value(standardWeight, " kg")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {standardWeightMethod}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2.5 }} />

        {/* ================= BODY COMPOSITION ================= */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle1" fontWeight={600} color="#1F497D">
            Body Composition
          </Typography>
          <Chip
            label={isEstimated ? "Estimated Values" : "Direct Device Measurements"}
            size="small"
            variant="outlined"
            color={isEstimated ? "default" : "primary"}
            sx={{ fontSize: "11px", height: 22 }}
          />
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Body Fat % {isEstimated ? "(Estimated)" : "(Measured)"}
            </Typography>
            <Typography variant="body1" fontWeight={500}>{value(bodyFat, " %")}</Typography>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Lean Body Mass {isEstimated ? "(Estimated)" : "(Measured)"}
            </Typography>
            <Typography variant="body1" fontWeight={500}>{value(leanBodyMass, " kg")}</Typography>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Muscle Mass {isEstimated ? "(Estimated)" : "(Measured)"}
            </Typography>
            <Typography variant="body1" fontWeight={500}>{value(muscleMass, " kg")}</Typography>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Bone Mass {isEstimated ? "(Estimated)" : "(Measured)"}
            </Typography>
            <Typography variant="body1" fontWeight={500}>{value(boneMass, " kg")}</Typography>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Body Moisture % {isEstimated ? "(Estimated)" : "(Measured)"}
            </Typography>
            <Typography variant="body1" fontWeight={500}>{value(bodyMoisture, " %")}</Typography>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Protein % {isEstimated ? "(Estimated)" : "(Measured)"}
            </Typography>
            <Typography variant="body1" fontWeight={500}>{value(protein, " %")}</Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2.5 }} />

        {/* ================= FAT & METABOLISM ANALYSIS ================= */}
        <Typography variant="subtitle1" fontWeight={600} color="#1F497D" gutterBottom>
          Metabolism & Fat Analysis
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={6} sm={4}>
            <Typography variant="caption" color="text.secondary" display="block">Pediatric BMR Estimate</Typography>
            <Typography variant="body1" fontWeight={500}>{value(bmr, " kcal/day")}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "10px" }}>
              {bmrMethod}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography variant="caption" color="text.secondary" display="block">Visceral Index (Estimated)</Typography>
            <Typography variant="body1" fontWeight={500}>{value(visceralFat)}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "10px" }}>
              (WHO Z-score based proxy rating)
            </Typography>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography variant="caption" color="text.secondary" display="block">Subcutaneous Fat % (Estimated)</Typography>
            <Typography variant="body1" fontWeight={500}>{value(subcutaneousFat, " %")}</Typography>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography variant="caption" color="text.secondary" display="block">Body Type (Estimated)</Typography>
            <Typography variant="body1" fontWeight={500}>{value(bodyType)}</Typography>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
        <Button
          onClick={handleDownload}
          variant="contained"
          startIcon={<PrintIcon />}
          sx={{ backgroundColor: "#1F497D", "&:hover": { backgroundColor: "#163A63" } }}
        >
          Print / Save PDF
        </Button>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MeasurementDetailsDialog;
